'use client';

import React from 'react';
import { faCircleNodes } from '@fortawesome/pro-solid-svg-icons';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { Card, NewProfileCardHeader } from 'src/components';
import { AffiliationSection } from './components';

const ProfileAffiliations = () => {
  const { entityDetails } = useProfile();

  const isProvider = entityDetails?.type === 'hcp';
  const isSiteOfCare = entityDetails?.type === 'site_of_care';

  // Split systems by their affiliation discriminator (owner_type) into separate
  // sub-sections. CORP => Parent, OWNR => Network; anything unspecified stays under the
  // generic "Systems" heading (today's behavior). Only non-empty groups render; when there
  // are no systems at all we still show an empty "Systems" section.
  const systems = entityDetails?.systems ?? [];
  const systemSections = [
    {
      key: 'parent',
      title: 'Parent',
      items: systems.filter((system) => system?.owner_type === 'CORP'),
    },
    {
      key: 'network',
      title: 'Network',
      items: systems.filter((system) => system?.owner_type === 'OWNR'),
    },
    {
      key: 'systems',
      title: 'Systems',
      items: systems.filter(
        (system) =>
          system?.owner_type !== 'CORP' && system?.owner_type !== 'OWNR'
      ),
    },
  ].filter((section) => section.items.length > 0);

  return (
    <Card className="p-6 bg-white rounded-lg outline outline-1 -outline-offset-1 outline-neutral-200 flex flex-col justify-start items-start gap-4 w-full h-full">
      <NewProfileCardHeader icon={faCircleNodes} title="Affiliations" />

      {systemSections.length > 0 ? (
        systemSections.map((section) => (
          <AffiliationSection
            key={section.key}
            title={section.title}
            items={section.items}
            testId={`affiliation-section-${section.key}`}
          />
        ))
      ) : (
        <AffiliationSection
          title="Systems"
          items={[]}
          testId="affiliation-section-systems"
        />
      )}

      <AffiliationSection
        title="Groups"
        items={entityDetails?.groups}
        testId="affiliation-section-groups"
      />

      {isSiteOfCare && (
        <>
          <AffiliationSection
            title="Group Purchasing Organization (GPO)"
            items={entityDetails?.gpos}
            testId="affiliation-section-gpos"
            isLink={false}
          />
          <AffiliationSection
            title="EHR Provider"
            items={entityDetails?.ehrs}
            testId="affiliation-section-ehrs"
            isLink={false}
          />
        </>
      )}

      {isProvider && (
        <>
          <AffiliationSection
            title="Sites*"
            items={entityDetails?.affiliations}
            testId="affiliation-section-sites"
          />
          <div className="self-stretch justify-start text-tertiary text-xs font-normal font-['Inter'] leading-tight">
            *Sites where the physician has claims volume or admitting
            privileges.
          </div>
        </>
      )}
    </Card>
  );
};

export default ProfileAffiliations;
